import type { KeyboardEvent, MouseEvent } from 'react';

import { FormFieldChoiceLabel } from '@components/core-ui/form-elements/FormFieldChoiceLabel';
import { FormFieldDisabledGroup } from '@components/core-ui/form-elements/FormFieldDisabledGroup';
import { getCheckBoxMarkClassName } from './helpers/getCheckBoxMarkClassName';
import { TProps } from './types/TProps';

const wrapperClassName = 'group relative block h-5 select-none pl-[25px]';

const checkBoxControlClassName = 'relative flex h-5 w-[25px] shrink-0 items-center';

export const CheckBox = ({
  checked = false,
  onChange,
  rounded,
  disabled = false,
  name,
  className = 'checkbox',
  testId,
  label,
  children,
}: TProps) => {
  const labelContent = label ?? children;
  const hasLabel = labelContent !== undefined && labelContent !== null && labelContent !== false;

  const activate = () => {
    if (disabled) {
      return;
    }
    onChange(!checked);
  };

  const handlePointerDown = (event: MouseEvent<HTMLLabelElement>) => {
    event.stopPropagation();
  };

  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    event.preventDefault();
    event.stopPropagation();
    activate();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLLabelElement>) => {
    if (disabled) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate();
    }
  };

  const markClassName = getCheckBoxMarkClassName({
    checked,
    disabled,
    rounded,
    hasLabel,
  });

  return (
    <FormFieldDisabledGroup disabled={disabled}>
      <label
        className={[
          'group relative select-none',
          disabled ? '' : 'cursor-pointer [&_svg]:cursor-pointer',
          hasLabel ? 'flex items-center gap-2' : wrapperClassName,
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        onMouseDown={handlePointerDown}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="checkbox"
        aria-checked={checked}
        data-testid={testId}
      >
        {hasLabel ? (
          <>
            <span className={checkBoxControlClassName}>
              <span className={markClassName} />
            </span>
            <FormFieldChoiceLabel disabled={disabled}>{labelContent}</FormFieldChoiceLabel>
          </>
        ) : (
          <span className={markClassName} />
        )}
      </label>
      <input type="checkbox" name={name} checked={checked} readOnly className="hidden" tabIndex={-1} aria-hidden />
    </FormFieldDisabledGroup>
  );
};
